<template>
  <div class="card" v-show="isSection('job-history')">
    <div class="overflow-scroll-y">
      <div class="card-header">
        <template v-if="!hideHeading">
          <h2>職歴を入力</h2>
          <p>「自動入力」を使うと、入社・退社表記を自動で作成できます。</p>
          <p v-if="formatId == 'rirekisho_hukugyo'">
            会社名を書きたくない場合は、「XX業界でのXX職」と書きましょう。
          </p>
          <el-checkbox v-model="formatData.notDisplayIjyo"
            >【以上】を表示しない</el-checkbox
          >
        </template>
        <div v-else class="compact-heading">
          <h3>{{ compactTitle }}</h3>
          <AutoJobHistories
            :birth-year="basicData.birthYear"
            :birth-month="basicData.birthMonth"
            :birth-day="basicData.birthDay"
            @job-histories-created="setJobHistories"
          />
        </div>
      </div>
      <div class="card-content-list">
        <!-- 【以上】を表示しない は学歴・職歴共通の見出し側（RirekishoEdit）へ移動 -->
        <p
          v-if="hideHeading && formatId === 'rirekisho_hukugyo'"
          class="compact-note"
        >
          会社名を書きたくない場合は、「XX業界でのXX職」と書きましょう。
        </p>
        <div class="edit-form-list">
          <div class="add-item">
            <AutoJobHistories
              v-if="!hideHeading"
              :birth-year="basicData.birthYear"
              :birth-month="basicData.birthMonth"
              :birth-day="basicData.birthDay"
              @job-histories-created="setJobHistories"
            />
            <div class="button-reset">
              <el-button
                v-if="
                  formatData.jobHistories && formatData.jobHistories.length > 1
                "
                @click="jobResetVisible = true"
                >リセット</el-button
              >
            </div>
            <NewRirekishoResetDialog
              :value="jobResetVisible"
              name="job-reset"
              title="職歴リセット"
              message="現在入力されている職歴をすべて削除します。よろしいですか？"
              @input="jobResetVisible = $event"
              @confirm="resetJobHistories"
            />
          </div>
          <transition-group name="fade" tag="div">
            <HistoryItem
              v-for="(item, index) in formatData.jobHistories"
              :key="item.uuid"
              :item="item"
              :length="formatData.jobHistories.length"
              :index="index"
              :id="item.uuid"
              :class="{ active: activeHistoryItem == item.uuid }"
              :minYear="basicData.birthYear"
              :createDate="formatData.createDate"
              :descriptionPlaceholder="'職歴を入力'"
              :descriptionMaxLength="descriptionMaxLength"
              @add-history-item="addJobHistory"
              @delete-history-item="deleteJobHistory"
              @move-history-item="moveJobHistory"
              @click.native="activateHistoryItem(item.uuid)"
            />
          </transition-group>
          <div class="add-list-item">
            <el-button
              icon="el-icon-plus"
              circle
              type="success"
              @click="addJobHistory()"
            />
          </div>
          <div
            class="attention-limit column"
            v-if="previewErrors.eduJobHistory"
          >
            <i class="el-icon-warning"></i>&ensp;{{
              previewErrors.eduJobHistory
            }}
          </div>
          <div v-if="formatId == 'rirekisho_job_change_old'">
            <p class="guidetxt">職歴の見本（参考にして作成しましょう）</p>
            <img
              src="/static/img/rirekishoguide/guide04.png"
              alt="職歴"
              class="guideimg"
            />
          </div>
          <div class="tips" v-if="formatId == 'rirekisho_job_change_old'">
            <details class="tips__accordion">
              <summary>履歴書の書き方ガイド</summary>
              <p>
                <span style="font-weight: bold; color: #004d40"
                  >① シンプルに書く</span
                ><br />
                基本的に雇用形態や職種、配属先などは記入不要です。職歴の具体的な成果は職務経歴書に記載し、履歴書はシンプルで見やすく記入していくのがおすすめです。<br /><br />
                退職理由を書く時は「一身上の都合により退職」と書くのが一般的で、具体的な理由は書かなくても問題ありません。
              </p>
            </details>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import HistoryItem from '@/components/formats/RirekishoParts/HistoryItem.vue'
import AutoJobHistories from '@/components/formats/RirekishoParts/AutoJobHistories.vue'
import NewRirekishoResetDialog from '@/components/format-edit/new-rirekisho/NewRirekishoResetDialog.vue'
import { useRirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'
import { useRirekishoHistoriesApi } from '@/composables/format-edit/useRirekishoHistories'
import type { BasicData } from '@/utils/format-edit/basicData'
import { isCardSectionActive } from '@/utils/format-edit/cardSections'

const descriptionMaxLength = 80

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    formatId?: string
    basicData: BasicData
    previewErrors: Record<string, string>
    /** 統合画面では見出し・リード文を出さず、短い見出しだけ帯に置く */
    hideHeading?: boolean
    compactTitle?: string
  }>(),
  {
    hideHeading: false,
    compactTitle: ''
  }
)

// 書式によって学歴・職歴は 1 画面へ統合される（カード定義の `sections`）
const isSection = (section: string) =>
  isCardSectionActive(props.formatId, props.currentCardId, section)

const formatData = useRirekishoFormatData()
const {
  activeHistoryItem,
  setJobHistories,
  resetJobHistories,
  addJobHistory,
  deleteJobHistory,
  moveJobHistory,
  activateHistoryItem
} = useRirekishoHistoriesApi()

const jobResetVisible = ref(false)
</script>

<style lang="scss" scoped>
@import '@/assets/scss/format-edit/rirekisho-guide';

// 統合画面: 見出しと「自動入力」をヘッダ帯の中に横並びで置く
.compact-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.compact-note {
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
}

.tips {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;

  &__accordion {
    background: #eef5e8;
    border-radius: 6px;
    padding: 5px 10px;
    margin-bottom: 10px;
    font-size: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }

    summary {
      position: relative;
      color: #004d40;
      font-weight: bold;

      &::after {
        position: absolute;
        content: '';
        background-image: url('/static/img/closebtn.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        width: 15px;
        height: 15px;
        right: 0;
      }
    }

    &:not([open]) {
      summary {
        &::after {
          background-image: url('/static/img/openbtn.svg');
          right: -15px;
          top: -5px;
        }
      }
    }
  }
}
</style>
